import { resolveTier } from '../../config/tierOverride'
import { tierForBuild, type Tier } from './tierForBuild'

/**
 * The tier THIS PAGE LOAD grants — #477. `main.tsx`'s `BillingProvider`
 * baseline, `TierBadge` and the Developer page all import these two, so the
 * baseline, the badge shown in the chrome, and the switch's own "in use"
 * state cannot disagree with one another.
 *
 * The tier is the BUILD's grant, not the whole entitlement set: a signed-in
 * subscriber's RevenueCat entitlements are added to it (except under a
 * developer override, which switches billing off). On staging the badge can
 * therefore read Free while a subscriber's gates are open.
 *
 * **Resolved once, at module evaluation.** `TIER` is not a hook and does not
 * re-read storage; a stored override written after this module has already
 * evaluated takes effect only on the next page load, which is why the
 * Developer page's switch calls `reload()` rather than just writing storage.
 *
 * **The condition is inline, and it is the production gate**, the same
 * pattern `api/client.ts`'s `API_BASE` uses for `resolveApiBase`: Vite folds
 * `import.meta.env.MODE` to a literal, so in a production build this line is
 * `"production" !== "production" ? … : BUILD_TIER` — the override branch is
 * dead, and `config/tierOverride` (its storage key, its data) is never
 * reached. `tier.test.ts`'s production test pins the *behaviour* (a stored
 * override does nothing) rather than trusting this sentence; nothing here
 * greps the built artifact the way `backendOverride.build.test.ts` does for
 * the backend switch — if that stronger guarantee is wanted for this
 * override too, it is a follow-up, not implied by this comment.
 *
 * `BUILD_TIER` is exported too: it is what `TierBadge` compares `TIER`
 * against to decide whether to show "override", the same way `BackendBadge`
 * compares `API_BASE` to `VITE_API_BASE`.
 */
export const BUILD_TIER: Tier = tierForBuild(import.meta.env.MODE)

export const TIER: Tier =
  import.meta.env.MODE !== 'production' ? resolveTier(BUILD_TIER) : BUILD_TIER

/**
 * How a tier is SPELLED — one place, so `TierBadge` (the chrome) and the
 * Developer page's "In use" line can never disagree. Both used to spell it
 * independently: the page title-cased via a local const, the badge rendered
 * the raw `Tier` string lowercase, and both tests queried case-insensitively
 * so neither noticed.
 */
export const TIER_LABEL: Readonly<Record<Tier, string>> = { free: 'Free', premium: 'Premium' }

export type { Tier }
